import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePageMetadata } from '../../app/PageMetaContext.jsx';
import { useApi, useMutation } from '../../lib/useApi.js';
import { api } from '../../lib/apiClient.js';
import { humanizeEnum, sourceSystemLabel } from '../../lib/fieldLabels.js';
import { useClientPagination } from '../../lib/usePagination.js';
import {
  PageHeader,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  LineagePanel,
  ProvenanceGraph,
  AlertCard,
  StatusChip,
  FormField,
  Textarea,
  Select,
  PageViewerControls,
  KpiHeroBand,
  deriveKpiTone,
  StatusDonutMini,
  InlineScoreCell,
  toast,
  activityRail,
} from '../../components/index.js';
import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import { ReconciliationDrawer } from './ReconciliationDrawer.jsx';
import { RESOURCE_FILTERS, filterResourceRecords } from './resourceEcosystemSignals.js';
import './admin.css';
import './dataAdmin.redesign.css';

// Days until a finding's SLA due date. Used for the inline SLA-pressure bar.
function daysUntil(dueAt) {
  if (!dueAt) return null;
  const due = new Date(dueAt).getTime();
  if (Number.isNaN(due)) return null;
  return Math.round((due - Date.now()) / 86400000);
}

function compactNodeLabel(value, fallback = 'Source') {
  const raw = String(value || fallback)
    .replace(/^(SRC|CONN|ART)-/i, '')
    .replace(/[_-]+/g, ' ')
    .trim();
  if (!raw) return fallback;
  return raw.length > 22 ? `${raw.slice(0, 19)}...` : raw;
}

export function LineageExplorerPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const focusTargetId = params.get('targetId');
  const lineageRes = useApi('/lineage', { query: { limit: 300 } });
  // AC-DATA-6.1 / AC-W9 — a freshly committed record can fall outside the primary 300-edge
  // inventory window once the seeded corpus already fills it. When focusing on a specific
  // record, additionally fetch the edges that target it directly so the focused chain stays
  // complete regardless of the inventory cap. Limit is generous (200) so a dense canonical
  // entity's full provenance fan renders in the S2 ProvenanceGraph without truncation.
  const focusedLineageRes = useApi('/lineage', {
    query: { limit: 200, targetEntityId: focusTargetId },
    skip: !focusTargetId,
  });
  // The command band must report the TRUE conflict/edge totals, not the counts
  // within the 300-edge inventory window — a staged conflict outside the window
  // would otherwise render as "Conflict edges 0" on a data-trust surface.
  const conflictCountRes = useApi('/lineage', { query: { limit: 1, conflictOnly: '1' } });
  const findingsRes = useApi('/data-quality/findings', { query: { limit: 30 } });
  const casesRes = useApi('/reconciliation-cases', { query: { limit: 30 } });
  const reconcileMut = useMutation();
  const createCaseMut = useMutation();
  const [selectedEdgeId, setSelectedEdgeId] = useState(null);
  const [drawerCaseId, setDrawerCaseId] = useState(null);
  // Case-less finding opened in the drawer's direct-finding mode (C1).
  const [drawerFinding, setDrawerFinding] = useState(null);
  const [resourceFilter, setResourceFilter] = useState('all');
  const [createCaseEdgeId, setCreateCaseEdgeId] = useState(null);
  const [caseOwner, setCaseOwner] = useState('');
  const [caseNote, setCaseNote] = useState('');
  const [caseSeverity, setCaseSeverity] = useState('medium');
  const { user } = useCurrentUser();

  const allEdges = useMemo(() => {
    const base = lineageRes.data?.data || [];
    const focused = focusedLineageRes.data?.data || [];
    if (!focused.length) return base;
    // Union the focused-target edges into the inventory (dedup by id) so an edge outside the
    // primary window still renders when the record is focused.
    const byId = new Map();
    for (const row of base) byId.set(row.id, row);
    for (const row of focused) if (!byId.has(row.id)) byId.set(row.id, row);
    return Array.from(byId.values());
  }, [lineageRes.data, focusedLineageRes.data]);
  // AC-DATA-6.1 — arriving with ?targetId= filters the inventory to the edges
  // that reference that record (as source, target, or edge id).
  const focusedEdges = useMemo(() => {
    if (!focusTargetId) return allEdges;
    return allEdges.filter((row) =>
      [row.id, row.targetEntityId, row.canonicalEntityId, row.sourceEntityId, row.sourceRecordId]
        .filter(Boolean)
        .includes(focusTargetId),
    );
  }, [allEdges, focusTargetId]);
  const filteredLineageEdges = useMemo(
    () => filterResourceRecords(focusedEdges, resourceFilter),
    [focusedEdges, resourceFilter],
  );
  const findings = useMemo(() => findingsRes.data?.data || [], [findingsRes.data]);
  const cases = useMemo(() => casesRes.data?.data || [], [casesRes.data]);

  const openFindings = useMemo(() => findings.filter((f) => f.status !== 'resolved'), [findings]);
  const resolvedFindings = useMemo(
    () => findings.filter((f) => f.status === 'resolved'),
    [findings],
  );
  const openFindingPager = useClientPagination(openFindings, {
    initialPageSize: 5,
    resetKey: `open-${openFindings.length}`,
  });
  const resolvedFindingPager = useClientPagination(resolvedFindings, {
    initialPageSize: 5,
    resetKey: `resolved-${resolvedFindings.length}`,
  });

  // Conflict edges: prefer the server's true total (conflictOnly count query);
  // fall back to the fetched-window count while it loads.
  const conflictEdgeCount = useMemo(() => {
    const serverTotal = conflictCountRes.data?.page?.total;
    if (Number.isFinite(Number(serverTotal))) return Number(serverTotal);
    return allEdges.filter((e) => e.conflictFlag).length;
  }, [conflictCountRes.data, allEdges]);
  const totalEdgeCount = useMemo(() => {
    const serverTotal = lineageRes.data?.page?.total;
    if (Number.isFinite(Number(serverTotal))) return Number(serverTotal);
    return allEdges.length;
  }, [lineageRes.data, allEdges]);

  // Severity rollup for the open-findings StatusDonutMini (data already fetched).
  const severitySegments = useMemo(() => {
    const counts = { high: 0, medium: 0, low: 0 };
    for (const f of openFindings) {
      const key = String(f.severity || '').toLowerCase();
      if (key in counts) counts[key] += 1;
      else counts.medium += 1;
    }
    return [
      { label: 'High', count: counts.high, tone: 'brick' },
      { label: 'Medium', count: counts.medium, tone: 'amber' },
      { label: 'Low', count: counts.low, tone: 'sage' },
    ];
  }, [openFindings]);

  const kpiItems = useMemo(
    () => [
      {
        id: 'open-findings',
        label: 'Open findings',
        value: openFindings.length,
        status: openFindings.length ? (severitySegments[0].count ? 'brick' : 'amber') : 'sage',
        statusLabel: openFindings.length ? 'Needs review' : 'Clear',
        footer: 'Source-to-canonical conflicts awaiting steward action',
      },
      {
        id: 'conflict-edges',
        label: 'Conflict edges',
        value: conflictEdgeCount,
        status: deriveKpiTone({ value: conflictEdgeCount, atRisk: conflictEdgeCount > 0 }),
        footer: `Across ${totalEdgeCount} mapped lineage edges`,
      },
      {
        id: 'high-severity',
        label: 'High severity',
        value: severitySegments[0].count,
        status: severitySegments[0].count ? 'brick' : 'sage',
        footer: 'Findings flagged high impact',
      },
      {
        id: 'resolved-cycle',
        label: 'Resolved this cycle',
        value: resolvedFindings.length,
        status: 'sage',
        footer: 'Reconciled findings in the current window',
      },
    ],
    [
      openFindings.length,
      conflictEdgeCount,
      totalEdgeCount,
      severitySegments,
      resolvedFindings.length,
    ],
  );

  // Map lineage edge IDs → reconciliation case ID for tooltip-drill handoff.
  const edgeToCaseId = useMemo(() => {
    const m = new Map();
    for (const c of cases) {
      const ids = Array.isArray(c.lineageEdgeIds) ? c.lineageEdgeIds : [];
      for (const eid of ids) m.set(eid, c.id);
    }
    return m;
  }, [cases]);

  // Paginated edge inventory with honest totals (AC-DATA-6.1).
  const edgePager = useClientPagination(filteredLineageEdges, {
    initialPageSize: 10,
    resetKey: `${resourceFilter}-${focusTargetId || 'all'}-${filteredLineageEdges.length}`,
  });

  const { nodes, edges } = useMemo(() => {
    const slice = filteredLineageEdges.slice(0, 10);
    const nodeMap = new Map();
    const built = [];
    slice.forEach((row, idx) => {
      const src = row.sourceEntityId || row.sourceRecordId || row.sourceSystem || `source-${idx}`;
      const tgt = row.targetEntityId || row.canonicalEntityId || `target-${idx}`;
      const transform = row.transformation || 'ETL';
      const tfId = `tf-${String(transform).replace(/\s+/g, '-')}`;
      if (!nodeMap.has(src))
        nodeMap.set(src, {
          id: src,
          column: 0,
          label: compactNodeLabel(sourceSystemLabel(row.sourceSystem) || src),
          sublabel: String(src).slice(0, 28),
          tone: 'sage',
          richTooltip: row.sourceNodeTooltip || null,
        });
      if (!nodeMap.has(tgt))
        nodeMap.set(tgt, {
          id: tgt,
          column: 2,
          label: compactNodeLabel(
            humanizeEnum(row.targetEntityType || row.canonicalEntityType || 'canonical'),
          ),
          sublabel: String(tgt).slice(0, 28),
          tone: 'primary',
          richTooltip: row.targetNodeTooltip || null,
        });
      if (!nodeMap.has(tfId))
        nodeMap.set(tfId, {
          id: tfId,
          column: 1,
          label: String(transform).slice(0, 24),
          sublabel: 'Transform',
          tone: 'amber',
          richTooltip: row.edgeTooltip || null,
        });
      built.push({
        id: row.id || `e-${idx}`,
        from: src,
        to: tfId,
        conflict: !!row.conflictFlag,
        dashed: false,
        richTooltip: row.edgeTooltip || null,
      });
      built.push({
        id: `${row.id || idx}-out`,
        from: tfId,
        to: tgt,
        conflict: !!row.conflictFlag,
        dashed: row.dashed,
        richTooltip: row.edgeTooltip || null,
      });
    });
    return { nodes: Array.from(nodeMap.values()), edges: built };
  }, [filteredLineageEdges]);

  usePageMetadata({
    title: 'Lineage',
    subtitle: 'Source systems, evidence movement, and data-quality handoffs across the ecosystem.',
  });

  function selectEdge(edge) {
    // Graph edges are drawn in two halves (`<id>` and `<id>-out`) — normalize
    // back to the lineage record id so case lookups stay accurate.
    setSelectedEdgeId(String(edge.id).replace(/-out$/, ''));
  }

  // S2 — ProvenanceGraph node click-through. Clicking the canonical convergence
  // node drills to the project record (or re-focuses the flow map for non-project
  // canonical entities); clicking a source node re-focuses on that source record.
  function handleNodeSelect(id, meta) {
    if (!id) return;
    if (meta?.kind === 'canonical' && /^(PRJ|INT)-/.test(id)) {
      navigate(`/projects/${id}`);
      return;
    }
    const next = new URLSearchParams(params);
    next.set('targetId', id);
    setParams(next, { replace: true });
    setSelectedEdgeId(null);
  }

  function clearFocus() {
    const next = new URLSearchParams(params);
    next.delete('targetId');
    setParams(next, { replace: true });
  }

  // AC-DATA-6.1 — an edge without a case never opens an unrelated one. It
  // opens the create-case form for that specific edge instead.
  function beginCreateCase(edgeId) {
    const baseEdgeId = String(edgeId || '').replace(/-out$/, '');
    const row = allEdges.find((e) => e.id === baseEdgeId);
    setSelectedEdgeId(baseEdgeId);
    setCreateCaseEdgeId(baseEdgeId);
    setCaseOwner(user?.id || '');
    setCaseNote('');
    setCaseSeverity(row?.conflictFlag ? 'high' : 'medium');
  }

  async function submitCreateCase() {
    try {
      const res = await createCaseMut.run(() =>
        api.post('/reconciliation-cases', {
          lineageEdgeIds: [createCaseEdgeId],
          ownerUserId: caseOwner.trim(),
          note: caseNote.trim(),
          severity: caseSeverity,
        }),
      );
      const created = res?.data || res;
      // Make the new case visible immediately: local cases list + drawer.
      casesRes.setData?.((prev) => {
        if (!prev || !Array.isArray(prev.data)) return prev;
        return { ...prev, data: [...prev.data, created] };
      });
      setCreateCaseEdgeId(null);
      setDrawerCaseId(created?.id || null);
      toast.success({
        title: 'Reconciliation case created',
        body: `${created?.id || 'New case'} · owner ${created?.ownerUserId || caseOwner}`,
      });
      activityRail.push({
        action: 'reconciliation_case.create',
        targetType: 'reconciliation_case',
        targetId: created?.id || 'unknown',
        actorUserId: user?.id || 'unknown',
        summary: `Created case for lineage edge ${createCaseEdgeId}`,
      });
    } catch {
      toast.error({
        title: 'Could not create reconciliation case',
        body: 'We could not create the reconciliation case. Please try again.',
      });
    }
  }

  function openCaseForEdgeId(edgeId) {
    const baseEdgeId = String(edgeId || '').replace(/-out$/, '');
    const caseId = edgeToCaseId.get(baseEdgeId);
    if (caseId) {
      setDrawerCaseId(caseId);
    } else {
      // Never open an unrelated case — offer to create one for this edge.
      beginCreateCase(baseEdgeId);
    }
  }

  function handleTooltipDrill(target) {
    if (target?.kind !== 'drawer' || target.drawerTarget !== 'reconciliation') return;
    const edgeId = target.params?.edgeId || null;
    if (edgeId) {
      setSelectedEdgeId(String(edgeId).replace(/-out$/, ''));
      openCaseForEdgeId(edgeId);
      return;
    }
    toast.warn({
      title: 'No reconciliation case',
      body: 'This lineage conflict does not reference a specific edge. Select an edge below to open or create its case.',
    });
  }

  // Clicking a finding NEVER mutates state. Case-backed findings open their
  // reconciliation case; case-less findings open the same drawer in
  // direct-finding mode, where resolving requires a written rationale (the old
  // fallback silently PATCHed the finding to resolved on click).
  function openCaseForFinding(finding) {
    const caseId =
      finding.relatedReconciliationGroupId || finding.relatedReconciliationCaseId || null;
    if (!caseId) {
      setDrawerFinding(finding);
      return;
    }
    setDrawerCaseId(caseId);
  }

  // Direct-finding resolve: the drawer collected the rationale; the API
  // enforces it again server-side.
  async function resolveDirectFinding(finding, rationale) {
    const res = await reconcileMut.run(() =>
      api.patch(`/data-quality/findings/${finding.id}/status`, {
        status: 'resolved',
        rationale,
      }),
    );
    const updated = res.data || res;
    findingsRes.setData?.((prev) => {
      if (!prev?.data?.data) return prev;
      return {
        ...prev,
        data: {
          ...prev.data,
          data: prev.data.data.map((f) => (f.id === finding.id ? updated : f)),
        },
      };
    });
    toast.success({
      title: 'Finding resolved',
      body: `${finding.title || finding.id} → ${humanizeEnum('resolved')}`,
    });
    activityRail.push({
      action: 'data_quality_finding.resolved',
      targetType: 'data_quality_finding',
      targetId: finding.id,
      actorUserId: user?.id || 'unknown',
      summary: `${finding.title?.slice(0, 64) || finding.id} → ${humanizeEnum('resolved')}`,
    });
    findingsRes.refresh?.();
  }

  async function handleResolved(body, caseData) {
    if (body?.directFinding || caseData?.directFinding) {
      const finding = drawerFinding || caseData;
      try {
        await resolveDirectFinding(finding, body.rationale);
      } catch (e) {
        toast.error({
          title: 'Could not resolve finding',
          body: 'We could not resolve this finding. Please try again.',
        });
        throw e;
      }
      return;
    }
    try {
      const res = await reconcileMut.run(() =>
        api.post(`/reconciliation-cases/${caseData.id}/actions`, body),
      );
      const updated = res.data || res;
      // Update local cases list.
      casesRes.setData?.((prev) => {
        if (!prev?.data?.data) return prev;
        return {
          ...prev,
          data: {
            ...prev.data,
            data: prev.data.data.map((c) => (c.id === caseData.id ? updated : c)),
          },
        };
      });
      // Clear conflict flag in local lineage if resolved.
      if (body.action === 'resolve') {
        const edgeIds = new Set(caseData.lineageEdgeIds || []);
        lineageRes.setData?.((prev) => {
          if (!prev?.data?.data) return prev;
          return {
            ...prev,
            data: {
              ...prev.data,
              data: prev.data.data.map((row) =>
                edgeIds.has(row.id) ? { ...row, conflictFlag: false } : row,
              ),
            },
          };
        });
        // Move related findings into the resolved bucket locally.
        findingsRes.setData?.((prev) => {
          if (!prev?.data?.data) return prev;
          return {
            ...prev,
            data: {
              ...prev.data,
              data: prev.data.data.map((f) =>
                f.relatedReconciliationGroupId === caseData.id
                  ? { ...f, status: 'resolved', resolvedAt: new Date().toISOString() }
                  : f,
              ),
            },
          };
        });
      }
      // Do NOT close the drawer here — the drawer shows a brief success
      // confirmation and auto-closes itself (via onClose) so a successful resolve
      // never flips back to a dangling "Loading case…" spinner.
      // Refetch server truth so the command-band totals, conflict-edge count, and
      // the focused graph reflect the resolution without a manual page reload.
      lineageRes.refresh?.();
      findingsRes.refresh?.();
      conflictCountRes.refresh?.();
      casesRes.refresh?.();
      // The focused-target fetch is a SEPARATE envelope feeding the focused
      // provenance graph lanes + edge inventory. Without refetching it here, the
      // focused view keeps rendering "Conflict" / "Open reconciliation case" for
      // an already-resolved edge until a manual reload, even though the aggregate
      // conflict count already refreshed above.
      focusedLineageRes.refresh?.();
      const actionPastLabel =
        { resolve: 'Resolved', defer: 'Deferred', reassign: 'Reassigned' }[body.action] ||
        humanizeEnum(body.action);
      toast.success({
        title:
          body.action === 'resolve'
            ? 'Conflict resolved'
            : body.action === 'defer'
              ? 'Conflict deferred'
              : 'Reassigned',
        body: `${caseData.title} · ${actionPastLabel}`,
      });
      activityRail.push({
        action: `reconciliation_case.${body.action}`,
        targetType: 'reconciliation_case',
        targetId: caseData.id,
        actorUserId: user?.id || 'unknown',
        summary: `${actionPastLabel} · ${caseData.title?.slice(0, 64) || caseData.id}`,
      });
      return updated;
    } catch (error) {
      toast.error({
        title: 'Reconciliation failed',
        body: 'We could not complete that reconciliation action. Please try again.',
      });
      // Rethrow so the drawer knows the resolve failed and returns to the form.
      throw error;
    }
  }

  // Skeleton ONLY on the initial load (no data yet). A background refresh —
  // e.g. the post-resolve refetch — must NOT swap the whole page for the
  // skeleton: doing so unmounted the ReconciliationDrawer mid-success, killing
  // its confirmation panel and auto-close timer (live UAT regression).
  if (lineageRes.loading && !lineageRes.data)
    return (
      <>
        <PageHeader title="Lineage" />
        <LoadingSkeleton rows={6} />
      </>
    );
  if (lineageRes.error && !lineageRes.data) return <ErrorState error={lineageRes.error} />;

  return (
    <>
      <PageHeader
        eyebrow="Data & Trust · Flow map"
        title="Resource flow map"
        subtitle="Where source systems, evidence, and canonical metrics move through DHA PRIME, with conflict highlighting and steward reconciliation actions."
      />

      {focusTargetId ? (
        <AlertCard
          severity="amber"
          title={`Focused on ${focusTargetId}`}
          body={`${focusedEdges.length} of ${totalEdgeCount} lineage edges reference this record. The graph and edge inventory below are filtered to that chain.`}
          actionLabel="Clear focus"
          onAction={clearFocus}
        />
      ) : null}

      <KpiHeroBand
        eyebrow="Data integrity · Command band"
        asOf={lineageRes.loadedAt}
        items={kpiItems}
        columns={4}
      />

      <section className="bento admin-bento">
        <div className="span-12 card resource-flow-context" data-testid="resource-flow-preview">
          <div>
            <div className="eyebrow">Resource flow preview</div>
            <h2 className="card-title">Filter the ecosystem from cradle to decision evidence</h2>
            <p className="muted">
              The graph below starts with the existing lineage inventory and frames it as the
              resource map: APIs, data feeds, evidence, licensing, and infrastructure handoffs.
            </p>
          </div>
          <div className="resource-filter-strip" role="group" aria-label="Resource flow filters">
            {RESOURCE_FILTERS.map((filter) => (
              <button
                key={filter.key}
                type="button"
                className={`btn ${resourceFilter === filter.key ? 'primary' : 'ghost'} sm`}
                onClick={() => {
                  setResourceFilter(filter.key);
                  setSelectedEdgeId(null);
                }}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        <div className="span-12 card lineage-graph-card">
          <header className="row spread">
            <div>
              <div className="eyebrow">
                {focusTargetId ? 'Provenance graph' : 'Resource flow graph'}
              </div>
              <h2 className="card-title">
                {focusTargetId
                  ? `${filteredLineageEdges.length} source edge${
                      filteredLineageEdges.length === 1 ? '' : 's'
                    } converging on ${focusTargetId}`
                  : `${nodes.length} nodes · ${edges.length} edges`}
              </h2>
            </div>
            {selectedEdgeId ? (
              <span className="chip outline sm">Selected: {selectedEdgeId}</span>
            ) : focusTargetId ? (
              <span className="muted small">
                Source systems reconciled into a single canonical record — click a source, the
                canonical node, or a conflict edge to act.
              </span>
            ) : (
              <span className="muted small">
                {`Graph renders the first ${Math.min(10, filteredLineageEdges.length)} of ${filteredLineageEdges.length} records in the current filter`}
              </span>
            )}
          </header>
          {focusTargetId ? (
            filteredLineageEdges.length === 0 ? (
              <EmptyState
                title="No lineage records"
                description="No lineage edges reference the focused record."
              />
            ) : (
              <ProvenanceGraph
                edges={filteredLineageEdges}
                focusEntityId={focusTargetId}
                selectedEdgeId={selectedEdgeId}
                onSelectEdge={selectEdge}
                onSelect={handleNodeSelect}
                onDrill={handleTooltipDrill}
              />
            )
          ) : nodes.length === 0 ? (
            <EmptyState
              title="No lineage records"
              description="No lineage records are available yet."
            />
          ) : (
            <LineagePanel
              nodes={nodes}
              edges={edges}
              selectedEdgeId={selectedEdgeId}
              onSelectEdge={selectEdge}
              onDrill={handleTooltipDrill}
              height={420}
            />
          )}
        </div>

        <div className="span-12 card">
          <header className="row spread">
            <div>
              <div className="eyebrow">Edge inventory</div>
              <h2 className="card-title">Lineage edges</h2>
            </div>
            <span className="muted small">
              {`Showing ${edgePager.items.length} of ${filteredLineageEdges.length} lineage edges`}
            </span>
          </header>
          {filteredLineageEdges.length === 0 ? (
            <EmptyState
              title="No lineage edges match"
              description={
                focusTargetId
                  ? 'No lineage edges reference the focused record.'
                  : 'No lineage records in the current filter.'
              }
            />
          ) : (
            <ul className="lineage-edge-list">
              {edgePager.items.map((row) => {
                const caseId = edgeToCaseId.get(row.id);
                return (
                  <li
                    key={row.id}
                    className={selectedEdgeId === row.id ? 'selected' : undefined}
                    data-testid={`lineage-edge-${row.id}`}
                  >
                    <div>
                      <strong className="mono">{row.id}</strong>
                      <div className="muted small mono">
                        {row.sourceEntityId || row.sourceRecordId || row.sourceSystem || 'Source'}
                        {' → '}
                        {row.targetEntityId || row.canonicalEntityId || 'Canonical'}
                      </div>
                    </div>
                    <div className="row" style={{ gap: 6 }}>
                      {row.conflictFlag ? <span className="chip amber sm">Conflict</span> : null}
                      {caseId ? (
                        <button
                          type="button"
                          className="btn secondary sm"
                          onClick={() => {
                            setSelectedEdgeId(row.id);
                            setDrawerCaseId(caseId);
                          }}
                        >
                          Open reconciliation case
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn ghost sm"
                          onClick={() => beginCreateCase(row.id)}
                        >
                          Create reconciliation case
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          <PageViewerControls
            page={edgePager.page}
            label="lineage edges"
            pageSize={edgePager.pageSize}
            onOffsetChange={edgePager.setOffset}
            onPageSizeChange={edgePager.setPageSize}
          />
          {createCaseEdgeId ? (
            <div className="lineage-create-case" data-testid="create-case-form">
              <div className="eyebrow">Create reconciliation case</div>
              <p className="muted small">
                Edge <span className="mono">{createCaseEdgeId}</span> has no reconciliation case.
                Assign an owner and describe the conflict to open one.
              </p>
              <FormField label="Owner" htmlFor="create-case-owner" required>
                <input
                  id="create-case-owner"
                  className="form-input"
                  value={caseOwner}
                  onChange={(e) => setCaseOwner(e.target.value)}
                  placeholder="e.g. USR-DATA-KIM"
                />
              </FormField>
              <FormField label="Note" htmlFor="create-case-note" required>
                <Textarea
                  id="create-case-note"
                  value={caseNote}
                  onChange={setCaseNote}
                  placeholder="What disagrees between the source and canonical record, and why does it matter?"
                  rows={3}
                />
              </FormField>
              <FormField label="Severity" htmlFor="create-case-severity">
                <Select
                  id="create-case-severity"
                  value={caseSeverity}
                  onChange={setCaseSeverity}
                  options={[
                    { value: 'high', label: 'High' },
                    { value: 'medium', label: 'Medium' },
                    { value: 'low', label: 'Low' },
                  ]}
                />
              </FormField>
              <div className="row" style={{ gap: 8 }}>
                <button
                  type="button"
                  className="btn primary sm"
                  disabled={createCaseMut.running || !caseOwner.trim() || !caseNote.trim()}
                  onClick={submitCreateCase}
                >
                  {createCaseMut.running ? 'Creating…' : 'Create case'}
                </button>
                <button
                  type="button"
                  className="btn ghost sm"
                  onClick={() => setCreateCaseEdgeId(null)}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : null}
        </div>

        <div className="span-7 card">
          <header className="data-admin__rollup-head">
            <div>
              <div className="eyebrow">Data quality findings</div>
              <h2 className="card-title">{openFindings.length} open</h2>
            </div>
            {openFindings.length ? (
              <span className="data-admin__rollup-sub">
                Click a finding to open its reconciliation case
              </span>
            ) : null}
          </header>
          {openFindings.length === 0 ? (
            <EmptyState title="No open findings" description="All conflicts resolved." />
          ) : (
            <div className="lineage-findings">
              {openFindingPager.items.map((f) => {
                const dueIn = daysUntil(f.dueAt);
                // Lower days-remaining = higher SLA pressure (inverted score).
                const slaState =
                  dueIn == null
                    ? 'ok'
                    : dueIn < 0 || dueIn <= 2
                      ? 'breach'
                      : dueIn <= 5
                        ? 'watch'
                        : 'ok';
                const slaTone =
                  slaState === 'breach' ? 'brick' : slaState === 'watch' ? 'amber' : 'sage';
                return (
                  <button
                    key={f.id}
                    type="button"
                    className="lineage-finding lineage-finding--scored"
                    data-sla={slaState}
                    onClick={() => openCaseForFinding(f)}
                    data-testid={`finding-${f.id}`}
                  >
                    <header className="row spread">
                      <strong>{f.title || f.id}</strong>
                      <StatusChip status={f.severity || 'warn'} />
                    </header>
                    <p className="muted small">
                      {f.description ||
                        f.recommendation ||
                        f.recommendedAction ||
                        'Investigate source-to-canonical mismatch.'}
                    </p>
                    {dueIn != null ? (
                      <div className="lineage-finding__sla">
                        <span className="lineage-finding__sla-label">
                          SLA · {dueIn < 0 ? `${Math.abs(dueIn)}d overdue` : `${dueIn}d left`}
                        </span>
                        <InlineScoreCell
                          value={Math.max(0, Math.min(14, dueIn ?? 0))}
                          max={14}
                          tone={slaTone}
                          suffix="d"
                          ariaLabel={`SLA pressure: ${dueIn} days remaining`}
                        />
                      </div>
                    ) : null}
                    <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                      <span className="chip outline sm">
                        {humanizeEnum(f.canonicalEntityType || 'entity')}
                      </span>
                      {(f.canonicalEntityIds || []).slice(0, 2).map((id) => (
                        <span key={id} className="chip outline sm mono">
                          {id}
                        </span>
                      ))}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          <PageViewerControls
            page={openFindingPager.page}
            label="open findings"
            pageSize={openFindingPager.pageSize}
            onOffsetChange={openFindingPager.setOffset}
            onPageSizeChange={openFindingPager.setPageSize}
          />
        </div>

        <div className="span-5 card">
          <header className="data-admin__rollup-head">
            <div>
              <div className="eyebrow">Open findings by severity</div>
              <h2 className="card-title">Severity mix</h2>
            </div>
          </header>
          <div className="data-admin__donut-wrap">
            <StatusDonutMini
              segments={severitySegments}
              size={120}
              totalLabel="Open"
              ariaLabel="Open findings by severity"
            />
          </div>
          <header className="data-admin__rollup-head" style={{ marginTop: '0.75rem' }}>
            <div>
              <div className="eyebrow">Recently resolved</div>
              <h2 className="card-title">{resolvedFindings.length} in last cycle</h2>
            </div>
          </header>
          {resolvedFindings.length === 0 ? (
            <p className="muted small">
              No reconciliations yet. Resolve a finding above to populate this rail.
            </p>
          ) : (
            <ul className="recon-resolved-list">
              {resolvedFindingPager.items.map((f) => (
                <li key={f.id}>
                  <strong>{f.title || f.id}</strong>
                  <div className="muted small">Resolved {f.resolvedAt?.slice(0, 10) || '—'}</div>
                </li>
              ))}
            </ul>
          )}
          <PageViewerControls
            page={resolvedFindingPager.page}
            label="resolved findings"
            pageSize={resolvedFindingPager.pageSize}
            onOffsetChange={resolvedFindingPager.setOffset}
            onPageSizeChange={resolvedFindingPager.setPageSize}
          />
        </div>
      </section>

      <ReconciliationDrawer
        open={!!(drawerCaseId || drawerFinding)}
        caseId={drawerCaseId}
        finding={drawerFinding}
        onClose={() => {
          setDrawerCaseId(null);
          setDrawerFinding(null);
        }}
        onResolved={handleResolved}
        submitting={reconcileMut.running}
      />
    </>
  );
}
